import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { allCategories } from "@/lib/news";

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `whitespace-nowrap px-3 py-2 text-sm font-label transition-colors cursor-pointer ${
    isActive
      ? "text-accent-600"
      : "text-foreground-600 hover:text-primary-600"
  }`;

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const kw = keyword.trim();
    if (!kw) return;
    navigate(`/?q=${encodeURIComponent(kw)}`);
    setKeyword("");
    setSearchOpen(false);
    setMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="flex h-16 w-full items-center justify-between gap-4 px-4 md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2 cursor-pointer">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-eye-2-line text-xl" />
          </span>
          <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">
            黑料官网
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          <NavLink to="/" className={navLinkClass} end>
            首页
          </NavLink>
          {allCategories.map((category) => (
            <NavLink
              key={category.id}
              to={`/category/${category.id}`}
              className={navLinkClass}
            >
              {category.name}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form
            onSubmit={submitSearch}
            className="hidden items-center rounded-md border border-background-200 bg-background-100 px-3 py-1.5 md:flex"
            role="search"
          >
            <i className="ri-search-line text-base text-foreground-400" />
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索黑料关键词"
              className="ml-2 w-40 bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400 lg:w-52"
            />
          </form>

          <button
            type="button"
            onClick={() => setSearchOpen((prev) => !prev)}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700 cursor-pointer md:hidden"
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg" />
          </button>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-foreground-700 cursor-pointer lg:hidden"
            aria-label="菜单"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-background-200 px-4 py-3 md:hidden">
          <form
            onSubmit={submitSearch}
            className="flex items-center rounded-md border border-background-200 bg-background-100 px-3 py-2"
            role="search"
          >
            <i className="ri-search-line text-base text-foreground-400" />
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索黑料关键词（演示）"
              className="ml-2 w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-500 px-3 py-1 text-xs font-label text-background-50 cursor-pointer"
            >
              搜索
            </button>
          </form>
        </div>
      )}

      {menuOpen && (
        <nav
          className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-2 lg:hidden"
          aria-label="移动端导航"
        >
          <NavLink to="/" className={navLinkClass} end>
            首页
          </NavLink>
          <div className="grid grid-cols-2 gap-1">
            {allCategories.map((category) => (
              <NavLink
                key={category.id}
                to={`/category/${category.id}`}
                className={navLinkClass}
              >
                {category.name}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}